import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { SectionEyebrow } from './SectionEyebrow';
import { TopicSelect } from './TopicSelect';
import { contactTopics, contacts } from '../data/navigation';
import { sendContactMessage } from '../utils/supabase';
type Status = 'idle' | 'submitting' | 'success' | 'error';
type Errors = Partial<Record<'name' | 'email' | 'phone' | 'consent', string>>;

// Navy footer: grid lines match the hero's faint rails
const LINE = 'border-[rgba(170,185,210,0.09)]';
// Same frame + cell geometry as the Philosophy / Ecosystem grids, so the lines line up
const FRAME = 'mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const ROW = 'grid md:grid-cols-2';
const LEFT = 'px-4 md:pl-8 md:pr-16';
const RIGHT = `md:-ml-8 md:border-l ${LINE}`;
// Lines inside the form box — a touch stronger so the fields read clearly
const BOX_LINE = 'border-cream/[0.14]';
const BOX = `field-box group/field border-t ${BOX_LINE} px-4 py-4 md:px-8`;
// One element per field: the placeholder doubles as the label (aria-label keeps it accessible)
const input = 'h-[30px] w-full border-0 bg-transparent p-0 text-[16px] text-cream placeholder:text-cream/45 focus:outline-none';
function Rule() {
  return <div aria-hidden="true" className={`relative w-full border-t ${LINE}`} data-id="element-29" data-mp="83vopxaqc7vq" />;
}

/** Footer contact: statement on the left, one tidy column of boxed fields on the right. */
export function ContactForm() {
  const reduce = useReducedMotion();
  const [topic, setTopic] = useState(contactTopics[0].label);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!name.trim()) next.name = 'Please add your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address.';
    if (phone.replace(/\D/g, '').length < 7) next.phone = 'Please enter a valid phone number.';
    if (!agreed) next.consent = 'Please accept the Privacy Policy.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    try {
      await sendContactMessage({
        topic,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim() || null,
        consent: agreed,
        page: window.location.pathname
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const err = (k: keyof Errors) => errors[k] ? <span className="mt-1 block text-[12px] text-[#f0a9a4]" data-id="element-30" data-mp="1pj705r4udjmn">{errors[k]}</span> : null;
  return <div className="relative z-10 pt-[56px] md:pt-[96px]" data-id="element-31" data-mp="514vtvz158ys">
      <Rule data-id="element-32" data-mp="4zlee21n0rvf" />

      <div className={FRAME} data-id="element-33" data-mp="4ys7n12oi2da">
        <div className={ROW} data-id="element-34" data-mp="9s4sd048lp0r">
          {/* Statement */}
          <Reveal className={`${LEFT} flex flex-col py-10 md:py-8`} data-id="element-35" data-mp="9tpnaqugr9z5">
            <h2 className="max-w-[520px] font-display text-[40px] leading-[1.02] tracking-[-0.025em] text-cream md:text-[56px]" data-id="element-36" data-mp="3s6vtw7wscbw2">
              Let's build what <span className="font-editorial italic tracking-[-0.01em]" data-id="element-37" data-mp="wx8a24b5z25">endures</span>
            </h2>
            <p className="mt-6 max-w-[400px] text-[14.5px] leading-[1.5] text-cream/70" data-id="element-38" data-mp="sqh1ilv5eywc">
              We work with a small circle of partners. Every message is read, and answered personally.
            </p>
            <p className="mt-auto pt-10 text-[13px] text-cream/60" data-id="element-39" data-mp="jpivjy15ghb4">
              Prefer a direct line? Reach us at{' '}
              <a href={`mailto:${contacts[0].email}`} className="link-soft text-cream" data-id="element-40" data-mp="2zgnj514kwtri">
                {contacts[0].email}
              </a>
            </p>
          </Reveal>

          {/* Form — one framed container, top aligned with the heading */}
          <div className={`${RIGHT} px-4 pb-8 pt-0 md:py-8 md:pl-6 md:pr-6`} data-id="element-41" data-mp="3jrrdycpmcmqx">
            <Reveal delay={0.1} className={`border ${BOX_LINE} text-cream`} data-id="element-42" data-mp="3exde9y3xbtgg">
              <AnimatePresence mode="wait" initial={false} data-id="element-43" data-mp="3ayeest0yoowe">
                {status === 'success' ? <motion.div key="done" initial={reduce ? false : {
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} role="status" className="flex h-full flex-col justify-center px-4 py-12 md:px-8" data-id="element-44" data-mp="35zjluy9a5jyk">
                    <SectionEyebrow label="Message received" tone="dark" data-id="element-45" data-mp="3onybpi7v2rxs" />
                    <p className="mt-4 max-w-[420px] text-[18px] leading-[1.45] text-cream" data-id="element-46" data-mp="2i978v8z6gx14">
                      Thank you, {name.split(' ')[0]}. We'll reply to {email} personally.
                    </p>
                  </motion.div> : <motion.form key="form" onSubmit={submit} noValidate exit={reduce ? undefined : {
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-47" data-mp="233jmskz7eosr">
                    {/* Form title */}
                    <div className="px-4 py-4 md:px-8" data-id="element-48" data-mp="2hwdd71g0kpc7">
                      <SectionEyebrow label="Contact" tone="dark" data-id="element-49" data-mp="2lvcbi378pnuo" />
                    </div>

                    {/* Enquiry type — dropdown */}
                    <div className={`${BOX} z-20`} data-id="element-50" data-mp="2vubo1wv4m3my">
                      <TopicSelect id="contact-topic" label="Type of enquiry" options={contactTopics} value={topic} onChange={setTopic} data-id="element-51" data-mp="2vvmpvr50h1bf" />
                    </div>

                    <div className={BOX} data-id="element-52" data-mp="16fx37nn8b0wq">
                      <input id="contact-name" aria-label="Name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoComplete="name" aria-invalid={!!errors.name} className={input} data-id="element-53" data-mp="yepgy0tkh0l0" />
                      {err('name')}
                    </div>

                    <div className={BOX} data-id="element-54" data-mp="1hb75k9d4ckd1">
                      <input id="contact-email" aria-label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" autoComplete="email" aria-invalid={!!errors.email} className={input} data-id="element-55" data-mp="1cf8hynzm76do" />
                      {err('email')}
                    </div>

                    <div className={BOX} data-id="element-56" data-mp="2erjeb6mrnmow">
                      <input id="contact-phone" aria-label="Phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" autoComplete="tel" aria-invalid={!!errors.phone} className={input} data-id="element-57" data-mp="2jqcvpa56bxuc" />
                      {err('phone')}
                    </div>

                    <div className={BOX} data-id="element-58" data-mp="1n0vfw2ch8vex">
                      <textarea id="contact-message" aria-label="Message (optional)" rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="A few words, if you wish" className={`${input} h-auto resize-none leading-[1.45]`} data-id="element-59" data-mp="1mxr8kw3ptjk1" />
                    </div>

                    {/* Consent + send */}
                    <div className={`flex flex-col border-t sm:flex-row ${BOX_LINE}`} data-id="element-60" data-mp="1ohkdnturn5t5">
                      <div className="field-box flex flex-1 flex-col justify-center px-4 py-4 md:px-8" data-id="element-61" data-mp="1jipwsxgczrbo">
                        <label className="group flex cursor-pointer items-start gap-3 text-[12px] leading-[1.5] text-cream/75" data-id="element-62" data-mp="1ejmqzask1h2n">
                          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} aria-invalid={!!errors.consent} className="peer sr-only" data-id="element-63" data-mp="19so5lwsplssj" />
                          <span aria-hidden="true" className={`mt-[2px] flex h-[14px] w-[14px] shrink-0 items-center justify-center border transition-colors duration-200 peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 ${agreed ? 'border-cream' : errors.consent ? 'border-[#f0a9a4]' : 'border-cream/45 group-hover:border-cream/80'}`} data-id="element-64" data-mp="vsdecsnhmc6u">
                            <span className={`h-[6px] w-[6px] bg-cream transition-opacity duration-200 ${agreed ? 'opacity-100' : 'opacity-0'}`} data-id="element-65" data-mp="2opha9o7l03ye" />
                          </span>
                          <span data-id="element-66" data-mp="2xiayib50adoe">
                            I agree that KAYS &amp; KAYS may use my details to respond to this enquiry, as described in the{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="underline decoration-cream/35 decoration-1 underline-offset-[3px] transition-colors duration-300 hover:text-cream hover:decoration-cream" data-id="element-67" data-mp="2106945mbk7jq">
                              Privacy Policy
                            </a>
                            .
                          </span>
                        </label>
                        {err('consent')}
                        {status === 'error' && <span role="alert" className="mt-2 block text-[12px] leading-[1.45] text-[#f0a9a4]" data-id="element-68" data-mp="30rgdeeodil91">
                            Your message couldn't be sent. Please try again, or write to {contacts[0].email}.
                          </span>}
                      </div>
                      <button type="submit" disabled={status === 'submitting'} className={`btn-fill btn-fill-light group flex min-h-[64px] shrink-0 items-center justify-between gap-6 self-stretch border-t px-6 font-accent text-[11px] uppercase tracking-[0.08em] text-cream disabled:opacity-60 sm:w-[220px] sm:border-l sm:border-t-0 md:px-8 ${BOX_LINE}`} data-id="element-69" data-mp="2d2duz4lx0odh">
                        <span className="relative z-[1]" data-id="element-70" data-mp="1wj97oysgygkp">{status === 'submitting' ? 'Sending…' : 'Send message'}</span>
                        <ArrowUpRightIcon aria-hidden="true" strokeWidth={1.25} className="relative z-[1] h-4 w-4" data-id="element-71" data-mp="1rm918mrpjbt2" />
                      </button>
                    </div>
                  </motion.form>}
              </AnimatePresence>
            </Reveal>
          </div>
        </div>
      </div>
      <Rule data-id="element-72" data-mp="1ctysrn0jrznn" />
    </div>;
}